import React, { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * A small "i" button that opens a short explanation of a setting, with an
 * optional "Learn more" link to the full help page. Reusable for any setting
 * that isn't self-explanatory.
 *
 * MUI's Popover gives the accessibility for free: it is a modal dialog that
 * takes focus, closes on Esc and on a click outside, and hands focus back to
 * the button when it closes.
 *
 * The help link is a plain anchor opening a new tab rather than a router
 * Link: these buttons sit inside live rooms, and leaving the room to read
 * help would drop the user out of the presentation. It also keeps this
 * component free of react-router-dom, which Jest can't resolve here (#174).
 *
 * @param title - heading shown in the pop-up, and the subject of the aria-label
 * @param children - the short description
 * @param learnMoreHref - optional path of the full help page
 * @param size - icon size passed to the IconButton ('small' by default)
 * @param sx - extra styles for the IconButton (e.g. color: 'inherit' on a coloured header)
 */
export default function InfoButton({ title, children, learnMoreHref, size = 'small', sx }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const popoverId = open ? `info-${title.replace(/\s+/g, '-').toLowerCase()}` : undefined;

    return (
        <>
            <IconButton
                size={size}
                aria-label={`About ${title}`}
                aria-haspopup='dialog'
                aria-describedby={popoverId}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={sx}>
                <InfoOutlinedIcon fontSize='inherit' />
            </IconButton>
            <Popover
                id={popoverId}
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                slotProps={{ paper: { role: 'dialog', 'aria-label': title } }}>
                <Box sx={{ p: 2, maxWidth: 320 }}>
                    <Typography variant='subtitle2' component='h2' gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant='body2' component='div' color='text.secondary'>
                        {children}
                    </Typography>
                    {learnMoreHref && (
                        <Link
                            href={learnMoreHref}
                            target='_blank'
                            rel='noopener'
                            variant='body2'
                            sx={{ display: 'inline-block', mt: 1.5 }}>
                            Learn more
                        </Link>
                    )}
                </Box>
            </Popover>
        </>
    );
}
